import { ISurvey } from "../../../../../@types/Surveys";

/**
 * EN-5261: the patient preview runs in a phone-sized iframe (the answering screen sizes itself
 * from the window), on the same origin. The builder publishes the version here; the iframe's page
 * subscribes from its parent window, so a save or a restart re-renders the survey instead of
 * reloading the whole app in the iframe.
 */
export interface ISurveyPreviewBridge {
  survey: ISurvey;
  /** Changes on every publish: the preview restarts the survey from the start. */
  revision: number;
  onFinish(): void;
}

const KEY = "surveyBuilderPreview";
const LISTENERS = "surveyBuilderPreviewListeners";

type BridgeWindow = Window & {
  [KEY]?: ISurveyPreviewBridge;
  [LISTENERS]?: Set<() => void>;
};

let revision = 0;

export function publishSurveyPreview(
  bridge: Omit<ISurveyPreviewBridge, "revision">
) {
  const w = window as BridgeWindow;
  revision += 1;
  w[KEY] = { ...bridge, revision };
  w[LISTENERS]?.forEach((listener) => {
    try {
      listener();
    } catch {
      // A listener left by an iframe that reloaded; the new page subscribes again.
    }
  });
}

function parentWindow(): BridgeWindow | undefined {
  try {
    // Throws for another origin; undefined when not embedded by the builder.
    return window.parent as BridgeWindow;
  } catch {
    return undefined;
  }
}

export function readSurveyPreview(): ISurveyPreviewBridge | undefined {
  return parentWindow()?.[KEY];
}

export function subscribeSurveyPreview(listener: () => void) {
  const w = parentWindow();
  if (!w) return () => {};
  w[LISTENERS] = w[LISTENERS] ?? new Set();
  w[LISTENERS].add(listener);
  return () => w[LISTENERS]?.delete(listener);
}
